import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { AuthUser } from '../models/auth-user.model';
import { mergeAuthConfig, ResolvedBuupassAuthConfig } from '../auth-defaults';
import { BUUPASS_AUTH_CONFIG } from '../tokens/auth-config.token';
import { BuupassAuthConfig } from '../types/auth-config';
import { AuthStorageService } from './auth-storage.service';

type AuthRestoreSource = 'local-cache' | 'shared-cookie' | 'none';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly config: ResolvedBuupassAuthConfig;
  private currentUserSubject: BehaviorSubject<AuthUser | null>;
  private authStateSubject: BehaviorSubject<boolean>;
  private profileSyncInProgress = false;
  private syncedProfileToken: string | null = null;
  private readonly syncOnForeground = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }

    this.refreshAuthState();
  };

  readonly currentUser$: Observable<AuthUser | null>;
  readonly authState$: Observable<boolean>;

  constructor(
    private http: HttpClient,
    private storage: AuthStorageService,
    @Inject(BUUPASS_AUTH_CONFIG) private rawConfig: BuupassAuthConfig
  ) {
    this.config = mergeAuthConfig(this.rawConfig);
    const restoredState = this.readStoredAuthState();
    const user = restoredState.user;
    this.currentUserSubject = new BehaviorSubject<AuthUser | null>(user);
    this.authStateSubject = new BehaviorSubject<boolean>(this.computeAuthState(user));
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.authState$ = this.authStateSubject.asObservable();
    this.bootstrapProfileSync(user, restoredState.source);
    this.bindSharedSessionSync();
  }

  isAuthenticated(): boolean {
    return this.authStateSubject.value;
  }

  getCurrentUserToken(): string | null {
    if (!this.isAuthenticated()) {
      return null;
    }

    return this.currentUserSubject.value?.token || null;
  }

  getCurrentUser(): AuthUser | null {
    return this.currentUserSubject.value;
  }

  setAuthenticatedUser(user: AuthUser | null): void {
    if (!user) {
      this.logout();
      return;
    }

    this.writeLocalAuthState(user);
    this.storage.writeSharedSession(user, this.config.sharedCookie);
    this.currentUserSubject.next(user);
    this.authStateSubject.next(this.computeAuthState(user));
  }

  refreshAuthState(): void {
    this.restoreState(true);
  }

  restoreAuthStateFromStorage(): AuthUser | null {
    return this.restoreState(true);
  }

  logout(): void {
    this.clearLocalAuthState();
    this.storage.clearSharedSession(this.config.sharedCookie);
    this.storage.removeItem('referralCode');
    this.storage.removeItem('referralPayload');
    this.syncedProfileToken = null;
    this.currentUserSubject.next(null);
    this.authStateSubject.next(false);
  }

  checkEmail(email: string) {
    return this.http.get(`${this.config.endpoints.retrieveEmail}${email}`, this.getApiKeyOptions());
  }

  signUp(user: unknown) {
    return this.http.post(this.config.endpoints.signUp, user);
  }

  passwordReset(user: unknown) {
    return this.http.post(this.config.endpoints.resetPassword, user);
  }

  sendCode(webformCode: unknown) {
    return this.http.post(this.config.endpoints.codePass, webformCode);
  }

  sendUpdatePassword(webformCode: { token: string }) {
    return this.http.patch(`${this.config.endpoints.updateCodePass}${webformCode.token}`, webformCode);
  }

  login(user: unknown) {
    return this.http.post(this.config.endpoints.loginUser, user).pipe(
      tap((response: any) => {
        if (response?.data) {
          this.setAuthenticatedUser(response.data);
        }
      })
    );
  }

  getProfile(user: { token: string }) {
    return this.http.get(this.config.endpoints.getProfile, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${user.token}`
      })
    });
  }

  updateProfile(payload: unknown) {
    return this.http.patch(this.config.endpoints.updateProfile, payload, {
      headers: this.getAuthenticatedHeaders(true)
    });
  }

  getTrips(phone: string) {
    return this.http.get(`${this.config.endpoints.getTrips}${phone}`, {
      headers: this.getAuthenticatedHeaders(false)
    });
  }

  makeReservation(payload: unknown) {
    return this.http.post(this.config.endpoints.bookingApi, payload, {
      headers: this.getAuthenticatedHeaders(true)
    });
  }

  async hashPhoneNumber(phoneNumber: string): Promise<string> {
    return this.sha256(phoneNumber);
  }

  async hashEmail(email: string): Promise<string> {
    return this.sha256(email);
  }

  private getApiKeyOptions() {
    return {
      headers: new HttpHeaders({
        Authorization: `Api-Key ${this.config.apiKey}`
      })
    };
  }

  private getAuthenticatedHeaders(includeJsonContentType: boolean): HttpHeaders {
    const token = this.getCurrentUserToken();

    if (!token) {
      throw new Error('User not authenticated');
    }

    let headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    if (includeJsonContentType) {
      headers = headers.set('Content-Type', 'application/json');
    }

    return headers;
  }

  private async sha256(value: string): Promise<string> {
    const normalizedValue = value ?? '';

    if (typeof crypto === 'undefined' || !crypto.subtle) {
      throw new Error('Web Crypto API is not available in this environment');
    }

    const buffer = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(normalizedValue)
    );

    return Array.from(new Uint8Array(buffer))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  private restoreState(syncProfile: boolean): AuthUser | null {
    const restoredState = this.readStoredAuthState();
    this.currentUserSubject.next(restoredState.user);
    this.authStateSubject.next(this.computeAuthState(restoredState.user));

    if (syncProfile) {
      this.bootstrapProfileSync(restoredState.user, restoredState.source);
    }

    return restoredState.user;
  }

  private readStoredAuthState(): { user: AuthUser | null; source: AuthRestoreSource } {
    const localUser = this.readLocalStoredUser();
    const sharedAuthState = this.storage.readSharedAuthState(this.config.sharedCookie);
    const sharedUser = this.storage.readSharedSession(this.config.sharedCookie);

    if (this.config.sharedCookie.enabled) {
      if (sharedAuthState === false) {
        if (localUser) {
          this.clearLocalAuthState();
        }
        return { user: null, source: 'none' };
      }

      if (sharedUser?.token) {
        const mergedUser =
          localUser?.token === sharedUser.token
            ? { ...sharedUser, ...localUser }
            : sharedUser;

        this.writeLocalAuthState(mergedUser);
        return { user: mergedUser, source: 'shared-cookie' };
      }

      return { user: localUser, source: localUser ? 'local-cache' : 'none' };
    }

    return { user: localUser, source: localUser ? 'local-cache' : 'none' };
  }

  private readLocalStoredUser(): AuthUser | null {
    const storedUser = this.storage.getItem('user');
    if (!storedUser) {
      return null;
    }

    try {
      return JSON.parse(storedUser) as AuthUser;
    } catch {
      return null;
    }
  }

  private writeLocalAuthState(user: AuthUser): void {
    this.storage.setItem('isLoggedIn', 'true');
    this.storage.setItem('user', JSON.stringify(user));
  }

  private clearLocalAuthState(): void {
    this.storage.removeItem('user');
    this.storage.removeItem('isLoggedIn');
  }

  private computeAuthState(user: AuthUser | null): boolean {
    return this.storage.getItem('isLoggedIn') === 'true' && !!user?.token;
  }

  private bootstrapProfileSync(user: AuthUser | null, source: AuthRestoreSource): void {
    if (
      !user?.token ||
      this.profileSyncInProgress ||
      this.syncedProfileToken === user.token ||
      (source !== 'shared-cookie' && !this.config.session.syncProfileOnBootstrap)
    ) {
      return;
    }

    this.profileSyncInProgress = true;

    this.getProfile({ token: user.token })
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          const profile = response?.data;
          if (profile) {
            this.setAuthenticatedUser({
              ...profile,
              token: profile.token ?? user.token
            });
          }
          this.syncedProfileToken = user.token ?? null;
        },
        error: (error) => {
          if (error?.status === 401) {
            this.logout();
            return;
          }

          this.profileSyncInProgress = false;
        },
        complete: () => {
          this.profileSyncInProgress = false;
        }
      });
  }

  private bindSharedSessionSync(): void {
    if (!this.config.sharedCookie.enabled || typeof window === 'undefined') {
      return;
    }

    window.addEventListener('focus', this.syncOnForeground);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.syncOnForeground);
    }
  }
}
